import React, { useReducer, useContext, useEffect, useRef, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { categoryReducer } from './categoryReducer';
import { ICategory, IGetPaginatedCategories, TCategoryType } from 'types';
import { CategoryActionTypes } from './categoryActions';
import {
	categoryInitialState,
	CategoryContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './categoryContext';
import { getPaginatedCategories, deleteCategory } from 'backend/categories';
import { AxiosResponse } from 'axios';
import { useFetchData } from 'hooks/useFetchData';

const CategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(categoryReducer, categoryInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<ICategory>, IGetPaginatedCategories>(
		isMounted.current ? getPaginatedCategories : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			categoryTypes: state.IDsSelectedCategoryTypes,
			sortByCategories:
				state.sortBy === 'CategoriesA-Z' || state.sortBy === 'CategoriesZ-A' ? state.sortBy : null,
			sortByCategoryType:
				state.sortBy === 'CategoryTypeA-Z' || state.sortBy === 'CategoryTypeZ-A' ? state.sortBy : null,
			sortByCustomer: state.sortBy === 'CustomerA-Z' || state.sortBy === 'CustomerZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: CategoryActionTypes.SET_CATEGORIES,
				payload: {
					categories: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteCategory(state.IDForDeletion);
			const filteredCategories = state.categories.categories.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: CategoryActionTypes.SET_CATEGORIES,
				payload: {
					categories: {
						categoriesCount: state.categories.categoriesCount - 1,
						categories: filteredCategories,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: CategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: CategoryActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: CategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value ? value : null,
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: CategoryActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value ? value : null,
			},
		});
	};

	const setIDsSelectedCategoryTypes = (value: TCategoryType[]) => {
		dispatch({
			type: CategoryActionTypes.SET_IDS_SELECTED_CATEGORY_TYPES,
			payload: {
				IDsSelectedCategoryTypes: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: CategoryActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: CategoryActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: CategoryActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: CategoryActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: CategoryActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: CategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: CategoryActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<CategoryContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDCustomer,
				setIDRestaurant,
				setIDsSelectedCategoryTypes,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				categories: state.categories,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDsSelectedCategoryTypes: state.IDsSelectedCategoryTypes,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CategoryContext.Provider>
	);
};

export default CategoryState;
